import { InputHTMLAttributes, forwardRef } from 'react';

import clsx from 'clsx';

import { Label } from './label';
import { Fieldset } from './fieldset';

type InputFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  classNameFieldSet?: string;
  classNameLabel?: string;
  label?: string;
  name: string;
  errorMessage?: string;
  help?: string;
};

/**
 * InputField, consists of a fieldset with label and an input field
 * forwardRef is used to pass the ref to the input field, since we are using ReactFormHook
 * @props:
 * extends InputHTMLAttributes<HTMLInputElement>
 * - label: the label of the input field
 * - classNameFieldSet: the class of the fieldset
 * - name: the name of the input field (required)
 */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>((props, ref) => {
  const {
    id,
    name,
    label,
    required,
    classNameFieldSet = '',
    classNameLabel = '',
    className = '',
    type = 'text',
    errorMessage,
    help,
    ...rest
  } = props;

  return (
    <Fieldset className={classNameFieldSet}>
      {label && (
        <Label htmlFor={id ?? name} required={required} className={classNameLabel}>
          {label}
        </Label>
      )}
      <input
        {...rest}
        ref={ref}
        className={clsx(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
          className
        )}
        id={id ?? name}
        type={type}
        name={name}
        required={required}
      />
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      {help && <p className="text-sm text-muted-foreground">{help}</p>}
    </Fieldset>
  );
});

InputField.displayName = 'InputField';
